import OfferEvent from '@models/offer/OfferEvent';
import {
    uniq as _uniq,
    uniqBy as _uniqBy,
} from 'lodash';
import {
    Match,
    MatchBucket,
    MatchType,
    SearchResultsEvent,
    SearchResultsTournament,
    SuggestionsFunction,
} from '@store/modules/ui/sportOffer/search/types';
import { RouteName } from '@app/router/constants/RouteName';
import { normalizeString } from '@store/modules/ui/sportOffer/search/utils';
import { Location } from 'vue-router';

export function filterResults(
    query: string,
    eventsMap: Record<string, OfferEvent>,
    minSearchCharacters = 1,
): {
    eventsByCompetitors: SearchResultsEvent[],
    foundTournaments: SearchResultsTournament[],
} {
    if (query.length < minSearchCharacters) {
        return {
            eventsByCompetitors: [],
            foundTournaments: [],
        };
    }

    const events = Object.values(eventsMap);
    const foundSearchMatches = mapEventsToSearchMatches(events, query);
    const tournamentMatchEvents = foundSearchMatches
        .filter((match) => match.bucket === MatchBucket.Tournament)
        .map((match) => eventsMap[match.eventId]);
    const uniqueTournamentMatchEvents = _uniqBy(tournamentMatchEvents, 'tournamentId');
    const competitorMatchEvents = foundSearchMatches
        .filter((match) => match.bucket === MatchBucket.Competitor)
        .map((match) => eventsMap[match.eventId]);
    const uniqueCompetitorMatchEvents = _uniqBy(competitorMatchEvents, 'id').filter((event) => event.hasActiveOffer());

    const foundTournaments = uniqueTournamentMatchEvents.map((event) => ({
        id: event.tournamentId,
        name: event.tournamentName,
        categoryId: event.categoryId,
        sportId: event.sportId,
        location: getTournamentLocation(event),
    }));

    return {
        eventsByCompetitors: uniqueCompetitorMatchEvents,
        foundTournaments,
    };
}

function getTournamentLocation(event: OfferEvent): Location {
    return {
        name: RouteName.Sport.TournamentRedirect,
        params: {
            id: event.tournamentId.toString(),
        },
    };
}

export function suggestionsFunction(events: OfferEvent[], numberOfSuggestions = 5): SuggestionsFunction {
    return function (query: string) {
        return generateSuggestions(events, query, numberOfSuggestions);
    };
}

const matchOrder: Record<MatchType, number> = {
    [MatchType.Exact]: 0,
    [MatchType.FirstWordExact]: 1,
    [MatchType.AnyWordExact]: 2,
    [MatchType.StartsWith]: 3,
    [MatchType.AnyWordStartsWith]: 4,
    [MatchType.Includes]: 5,
};

const checkers: Record<MatchType, (text: string, matcher: string) => boolean> = {
    [MatchType.Exact]: (text, matcher) => text === matcher,
    [MatchType.FirstWordExact]: (text, matcher) => text.split(/\W+/)[0] === matcher,
    [MatchType.AnyWordExact]: (text, matcher) => text.split(/\W+/).some((word) => word === matcher),
    [MatchType.StartsWith]: (text, matcher) => text.startsWith(matcher),
    [MatchType.AnyWordStartsWith]: (text, matcher) => text.startsWith(matcher) || text.includes(' ' + matcher),
    [MatchType.Includes]: (text, matcher) => text.includes(matcher),
};

const sortedMatchTypes = Object.entries(matchOrder)
    .sort((a, b) => a[1] - b[1])
    .map((entry) => entry[0]) as MatchType[];

export function mapEventsToSearchMatches(
    events: OfferEvent[],
    searchQuery: string,
): Match[] {
    const foundSearchMatches: Match[] = [];

    events.forEach((event) => {
        const team1Name = event.team1Name;
        const team2Name = event.team2Name;
        const tournamentName = event.tournamentName;
        const eventId = event.id;

        addMatch(foundSearchMatches, eventId, team1Name, searchQuery, MatchBucket.Competitor);
        addMatch(foundSearchMatches, eventId, team2Name, searchQuery, MatchBucket.Competitor);
        addMatch(foundSearchMatches, eventId, tournamentName, searchQuery, MatchBucket.Tournament);
    });

    return foundSearchMatches;
}

export function generateSuggestions(
    events: OfferEvent[],
    searchQuery: string,
    numberOfSuggestions: number,
): string[] {
    const foundSearchMatches = mapEventsToSearchMatches(events, searchQuery);

    return getTopSearchMatches(foundSearchMatches, numberOfSuggestions);
}

function addMatch(
    matches: Match[],
    eventId: number,
    check: string,
    searchQuery: string,
    bucket: MatchBucket,
) {
    const normalizedCheck = normalizeString(check);
    const normalizedQuery = normalizeString(searchQuery);

    for (const matchType of sortedMatchTypes) {
        if (checkers[matchType](normalizedCheck, normalizedQuery)) {
            matches.push({
                eventId,
                match: check,
                type: matchType,
                bucket,
            });
            return;
        }
    }
}

function getTopSearchMatches(matches: Match[], numberOfMatches: number): string[] {
    const sortedMatches = matches.sort(
        (match1, match2) => {
            const match1StringNormalized = normalizeString(match1.match);
            const match2StringNormalized = normalizeString(match2.match);

            if (match1.type === match2.type) {
                return match1StringNormalized.localeCompare(match2StringNormalized);
            }

            return matchOrder[match1.type] - matchOrder[match2.type];
        },
    ).map((match) => match.match);

    return _uniq(sortedMatches).slice(0, numberOfMatches);
}
